import { useEffect, useRef, useState, type FormEvent } from 'react'
import { REFERRAL_REVERT_REASON_MAX, referralRevertRequestSchema, type ReferralAttribution } from '@manaro/contracts'
import { ApiError } from '@/shared/api/client.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Alert } from '@/shared/ui/states.tsx'
import { useRevertAttribution } from '../use-referrals.ts'

interface RevertDialogProps {
  attribution: ReferralAttribution
  code: string
  onClose: () => void
}

export function RevertDialog({ attribution, code, onClose }: RevertDialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const revert = useRevertAttribution()
  const invitee = attribution.invitee.name || attribution.invitee.email || `#${attribution.invitee.id}`

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  function submit(event: FormEvent) {
    event.preventDefault()
    if (revert.isPending) return

    const parsed = referralRevertRequestSchema.safeParse({ reason })
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Укажите причину отмены.')
      return
    }
    setValidationError(null)
    revert.mutate({ id: attribution.id, reason: parsed.data.reason }, { onSuccess: onClose })
  }

  const error =
    validationError ??
    (revert.error ? (revert.error instanceof ApiError ? revert.error.message : 'Не получилось — попробуйте ещё раз.') : null)

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onCancel={(event) => revert.isPending && event.preventDefault()}
      aria-labelledby="revert-dialog-title"
      className="m-auto w-[min(480px,calc(100vw-32px))] rounded-panel border border-line bg-surface p-0 text-ink shadow-panel backdrop:bg-[rgb(27_27_24/0.35)]"
    >
      <form onSubmit={submit} className="flex flex-col gap-4 px-6 py-5.5">
        <div className="flex flex-col gap-1.5">
          <h2 id="revert-dialog-title" className="font-display text-[19px] font-semibold tracking-[-0.01em]">
            Отменить начисление?
          </h2>
          <p className="text-[14px] text-ink-muted text-balance-pretty">
            {invitee} перестанет засчитываться коду <span className="font-mono text-ink">{code}</span>. Вернуть
            начисление из админки нельзя.
          </p>
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="flex items-baseline justify-between gap-2.5">
            <span className="text-[13px] font-medium text-ink-soft">Причина</span>
            <span className="tabular text-[12px] text-ink-faint">
              {reason.length}/{REFERRAL_REVERT_REASON_MAX}
            </span>
          </span>
          <textarea
            value={reason}
            onChange={(event) => {
              setReason(event.target.value)
              setValidationError(null)
            }}
            rows={3}
            maxLength={REFERRAL_REVERT_REASON_MAX}
            required
            autoFocus
            placeholder="Например: накрутка — несколько регистраций с одного IP"
            className="resize-y rounded-control border border-line-strong bg-surface-soft px-3.5 py-2.5 text-[14.5px] text-ink outline-none placeholder:text-ink-muted focus:border-brand focus:shadow-[0_0_0_3px_var(--color-brand-ring)]"
          />
        </label>

        {error && <Alert>{error}</Alert>}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" disabled={revert.isPending} onClick={() => dialogRef.current?.close()}>
            Не отменять
          </Button>
          <Button type="submit" size="sm" loading={revert.isPending}>
            Отменить начисление
          </Button>
        </div>
      </form>
    </dialog>
  )
}
